import { createClient } from "@/lib/supabase/client";
import { v4 as uuidv4 } from "uuid";
import { FileInfo } from "../../functions/src/services/file_info";
export async function uploadFile({
  bucketName,
  fileName,
  parentUuid,
  file,
}: {
  bucketName: string;
  fileName: string;
  parentUuid: string;
  searchParameter: string;
  file: File;
}): Promise<string> {
  const supabase = createClient();
  try {
    const fileFolderUuid = uuidv4();
    const path = `${parentUuid}/${fileFolderUuid}/${fileName}`;
    const { data, error: uploadError } = await supabase.storage
      .from(bucketName)
      .upload(`${path}`, file, {
        cacheControl: "3600",
        upsert: true,
      });
    if (uploadError) throw uploadError;

    return data.id;
  } catch (error) {
    console.error("Failed to upload file:", error);
    throw error;
  }
}

export async function getSignedUrl({
  bucketId,
  name,
}: {
  bucketId: string;
  name: string;
}): Promise<{ signedUrl: string }> {
  const isDevelopment = process.env.NODE_ENV !== "development";

  const supabase = createClient();
  const exists = await doesFileExists({ bucketId, name });
  if (!exists) {
    return { signedUrl: "" };
  }
  try {
    const data = await supabase.storage
      .from(bucketId)
      .createSignedUrl(name, 3600);

    if (data.data?.signedUrl === undefined) {
      throw new Error("Failed to get signed URL");
    }

    return { signedUrl: data!.data!.signedUrl };
  } catch (error) {
    if (isDevelopment) {
      console.error("Error in getSignedUrl:", error);
    }
    return { signedUrl: "" };
  }
}

export async function getFileInfoWithoutSupabaseClient(
  file_id: string
): Promise<FileInfo | null> {
  if (!file_id) return null;
  try {
    const supabase = createClient();
    const { data: objectResult } = await supabase
      .schema("storage")
      .from("objects")
      .select("bucket_id, name, metadata")
      .eq("id", file_id)
      .single();

    if (!objectResult?.bucket_id)
      throw new Error("Object bucket_id not found.");
    if (!objectResult?.name) throw new Error("Object name not found.");

    const metadata = objectResult.metadata as { [key: string]: any };
    return {
      bucket: objectResult.bucket_id,
      name: objectResult.name,
      fileType: metadata?.mimetype,
    };
  } catch (error) {
    console.error("Failed to get public URL:", error);
    throw error;
  }
}

export async function doesFileExists({
  bucketId,
  name,
}: {
  bucketId: string;
  name: string;
}): Promise<boolean> {
  const supabase = createClient();
  try {
    const { data, error } = await supabase.rpc("check_file_exists", {
      bucket_id_variable: bucketId,
      name_variable: name,
    });
    if (error) throw error;
    return data as unknown as boolean;
  } catch (error) {
    console.error("Error checking if file exists:", error);
    return false;
  }
}
